/**
 * SDL consent API client (spec D8), typed-error shape borrowed from the GL §3
 * pattern: every failure resolves to a stated terminal error, never a silent
 * spinner. Paths carry the /qlearn context prefix like every other real
 * endpoint in urlList.js (the GL fetch's missing prefix is a known defect
 * under separate remediation — deliberately not copied here).
 *
 * The server is authoritative: while the feature is dark it answers a uniform
 * 404 to every one of these, which maps to "unavailable" here — and no SDL
 * surface is even mounted unless the FE flag is on (sdl-audience.js).
 */
import url from "../../constants/urlList";

export const sdlErrorForStatus = (status) => {
  if (status === 401 || status === 403) return "disabled";
  if (status === 429) return "cap";
  return "unavailable"; // 404 (dark) / 5xx / 0 (network) / anything else
};

export const sdlFetch = async (path, { method = "GET", body } = {}) => {
  try {
    const opts = { method, headers: { "Content-Type": "application/json" } };
    if (body !== undefined) {
      opts.body = JSON.stringify(body);
    }
    const resp = await fetch(`${url.domain}/qlearn${path}`, opts);
    const data = await resp.json().catch(() => null);
    if (resp.status >= 200 && resp.status < 300) {
      return { ok: true, data, status: resp.status };
    }
    return { ok: false, sdlError: sdlErrorForStatus(resp.status), status: resp.status, data };
  } catch (_) {
    return { ok: false, sdlError: "unavailable", status: 0 };
  }
};

// --- Spec D8 surface ---

export const grantSessionConsent = (eventId, scope) =>
  sdlFetch(`/sessions/${encodeURIComponent(eventId)}/consent`, {
    method: "POST",
    body: { scope },
  });

export const revokeSessionConsent = (consentId) =>
  sdlFetch(`/sessions/consent/${encodeURIComponent(consentId)}`, { method: "DELETE" });

export const listOwnSessionConsents = () => sdlFetch("/me/session-consents");

export const getStandingPreference = () => sdlFetch("/me/session-consents/preference");

/** preference: "OPTED_IN" | "DECLINED" | null (clears to unset). */
export const setStandingPreference = (preference, scope) =>
  sdlFetch("/me/session-consents/preference", {
    method: "PUT",
    body: { preference, scope },
  });
